import React, { useId, useMemo, useState } from "react";
import "./ScoreActionLog.css";
import { scoreAuditDate } from "./scoreAuditDate.js";
export { scoreAuditDate } from "./scoreAuditDate.js";

function deviceKey(event) {
  return event.deviceId || event.deviceName || "unknown";
}

function deviceLabel(event) {
  const suffix = event.deviceId ? ` (${event.deviceId.slice(-8)})` : "";
  return `${event.deviceName || "Unknown device"}${suffix}`;
}

function actionTone(action) {
  if (action === "sync_failed") return "failure";
  if (action === "confirmed" || action === "sync_succeeded") return "success";
  if (action === "confirmation_cleared") return "warning";
  return "neutral";
}

function actionKey(event) {
  return event.source === "discarded_local_decision" ? "participant_outcome_discarded" : event.action || "unknown";
}

export default function ScoreActionLog({ events, competitorName, title = "Score Action Log" }) {
  const titleId = useId();
  const [search, setSearch] = useState("");
  const [device, setDevice] = useState("all");
  const [action, setAction] = useState("all");
  const [order, setOrder] = useState("newest");
  const devices = useMemo(() => [...new Map(events.map(event => [deviceKey(event), deviceLabel(event)])).entries()], [events]);
  const actions = useMemo(() => [...new Map(events.map(event => [actionKey(event), event.title || "Score action"])).entries()], [events]);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return events.filter(event => {
      if (device !== "all" && deviceKey(event) !== device) return false;
      if (action !== "all" && actionKey(event) !== action) return false;
      return !query || [event.title, event.detail, event.competitorName, event.entryLabel,
        event.stageName, event.stageId, event.deviceName, event.deviceId, event.id, event.participantId, event.entryId,
        event.actorLabel, event.actorUserId, event.after?.notes, Array.isArray(event.after) ? event.after[3] : null]
        .filter(Boolean).join(" ").toLowerCase().includes(query);
    }).sort((a, b) => {
      const left = scoreAuditDate(a.createdAt)?.getTime();
      const right = scoreAuditDate(b.createdAt)?.getTime();
      if (left === undefined) return right === undefined ? 0 : 1;
      if (right === undefined) return -1;
      return order === "oldest" ? left - right : right - left;
    });
  }, [events, search, device, action, order]);
  const hasFilters = search !== "" || device !== "all" || action !== "all";

  return (
    <section className="score-action-log" aria-labelledby={titleId}>
      <header className="score-action-log-heading">
        <div>
          <span className="score-action-log-access">Owner only</span>
          <h2 id={titleId}>{title}</h2>
        </div>
        <span>{events.length} {events.length === 1 ? "event" : "events"} · {devices.length} {devices.length === 1 ? "device" : "devices"}</span>
      </header>
      <div className="score-action-log-filters">
        <label className="score-action-log-search">Search log
          <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Action, device, competitor or ID" />
        </label>
        <label>Device
          <select value={device} onChange={event => setDevice(event.target.value)}>
            <option value="all">All devices</option>
            {devices.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label>Action
          <select value={action} onChange={event => setAction(event.target.value)}>
            <option value="all">All actions</option>
            {actions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label>Order
          <select value={order} onChange={event => setOrder(event.target.value)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </label>
      </div>
      {device !== "all" && <div className="score-action-log-selected-device">Device: {devices.find(([key]) => key === device)?.[1]}</div>}
      <div className="score-action-log-status">
        <span role="status">{filtered.length} of {events.length} events</span>
        {hasFilters && <button type="button" onClick={() => { setSearch(""); setDevice("all"); setAction("all"); }}>Clear filters</button>}
      </div>
      {filtered.length === 0 ? (
        <div className="score-action-log-empty">{events.length ? "No events match the selected filters." : "No actions have been synced yet."}</div>
      ) : (
        <div className="score-action-log-events">
          {filtered.map((event, index) => {
            const date = scoreAuditDate(event.createdAt);
            const context = [event.competitorName || competitorName, event.entryLabel, event.stageName].filter(Boolean).join(" · ");
            const note = Array.isArray(event.after) ? event.after[3] : event.after?.notes;
            const before = Array.isArray(event.before) ? event.before[0] : event.before?.outcomeStatus;
            const after = Array.isArray(event.after) ? event.after[0] : event.after?.outcomeStatus;
            const ids = [["Event ID", event.id], ["Device ID", event.deviceId], ["Match ID", event.matchId],
              ["Stage ID", event.stageId], ["Participant ID", event.participantId], ["Entry ID", event.entryId]];
            return (
              <details className={`score-action-log-event score-action-log-event--${actionTone(event.action)}`} key={event.id || index}>
                <summary>
                  <div className="score-action-log-event-heading">
                    <strong>{event.title || event.action || "Score action"}</strong>
                    <time dateTime={date?.toISOString()}>{date ? date.toLocaleString(undefined, {
                      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit", timeZoneName: "short"
                    }) : "Time unavailable"}</time>
                  </div>
                  <p>{event.detail || "No detail recorded."}</p>
                  {note && <p className="score-action-log-note"><strong>Reason / note:</strong> {note}</p>}
                  <div className="score-action-log-event-meta">
                    <span>{context}</span>
                    <span>{deviceLabel(event)}</span>
                  </div>
                </summary>
                <dl className="score-action-log-trace">
                  <div><dt>Recorded at (UTC)</dt><dd>{date?.toISOString() || "Not recorded"}</dd></div>
                  {event.actorUserId && <div><dt>Account</dt><dd>{event.actorLabel || event.actorUserId}</dd></div>}
                  {event.receivedAt && <div><dt>Received by cloud</dt><dd>{scoreAuditDate(event.receivedAt)?.toISOString() || "Not recorded"}</dd></div>}
                  {before && <div><dt>Previous status</dt><dd>{before.toUpperCase()}</dd></div>}
                  {after && <div><dt>New status</dt><dd>{after.toUpperCase()}</dd></div>}
                  {event.before?.decisionId && <div><dt>Previous decision</dt><dd>{event.before.decisionId}</dd></div>}
                  {ids.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || "Not recorded"}</dd></div>)}
                </dl>
              </details>
            );
          })}
        </div>
      )}
    </section>
  );
}
